import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api, { resolveImg } from "@/lib/api";
import { getPlayableUrl } from "@/lib/offlineCache";

const PlayerContext = createContext(null);

/**
 * Inaudible ultrasonic watermark.
 *
 * We layer an inaudible ~18.5 kHz sine wave (amplitude 0.0018 — below the noise
 * floor of most mobile speakers) on top of the audio output. Its amplitude is
 * modulated to encode a compact hash of the currently-logged-in user id, so
 * if a leaked audio file surfaces we can identify which account was the
 * origin. This is a best-effort tracing tool, not a DRM.
 */
function useUltrasonicWatermark(audioRef, ctxRef, activeWhen) {
  // The AudioContext/MediaElementSourceNode themselves now live in
  // PlayerProvider (created once, eagerly, for the <audio> element's whole
  // lifetime — see the comment there for why). This hook only owns the
  // oscillator nodes, started/stopped per activeWhen.
  const nodesRef = useRef(null);
  useEffect(() => {
    if (!activeWhen) {
      try { nodesRef.current?.osc.stop(); } catch { /* noop */ }
      try { nodesRef.current?.lfo.stop(); } catch { /* noop */ }
      nodesRef.current = null;
      return;
    }
    const ctx = ctxRef.current;
    if (!audioRef.current || !ctx) return;
    let cancelled = false;
    (async () => {
      try {
        // Grab watermark id from /api/auth/me (falls back to "guest").
        let wm = "guest";
        try {
          const { data } = await api.get("/auth/me");
          wm = String(data?.id || data?.email || "guest").slice(-8);
        } catch { /* noop */ }
        if (cancelled) return;

        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 18500;
        const gain = ctx.createGain();
        gain.gain.value = 0.0018;
        // Amplitude-modulate to encode the wm hash (very slow ~1 Hz).
        const lfo = ctx.createOscillator();
        lfo.type = "square";
        const hash = [...wm].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 5381);
        lfo.frequency.value = 0.6 + ((hash % 40) / 100); // 0.6-1.0 Hz, per-user
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = 0.001;
        lfo.connect(lfoGain).connect(gain.gain);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        lfo.start();
        nodesRef.current = { osc, gain, lfo, lfoGain };
      } catch { /* Web Audio not available — silently degrade. */ }
    })();
    return () => { cancelled = true; };
  }, [audioRef, ctxRef, activeWhen]);
}

export function PlayerProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const discoveryRef = useRef(false);
  const audioCtxRef = useRef(null);

  // Audio element setup
  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.volume = volume;
    // Anti-piracy: prevent download / native context-menu actions on the audio element.
    a.crossOrigin = "anonymous";
    a.controlsList = "nodownload noplaybackrate noremoteplayback";
    a.disableRemotePlayback = true;
    a.oncontextmenu = (e) => { e.preventDefault(); return false; };
    audioRef.current = a;

    // Create the Web Audio graph (used by the ultrasonic watermark below)
    // right away, exactly once for this element's whole lifetime — merely
    // *constructing* an AudioContext never needs a user gesture, only
    // resume()/starting playback does (handled by the unlock effect below).
    // Doing this eagerly, instead of lazily on first play, keeps context
    // creation out of any async/await chain — iOS Safari requires
    // resume() to happen synchronously inside a real user-gesture handler,
    // and once createMediaElementSource() has bound this element to a
    // context, ALL of its audio depends on that context actually running.
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        const src = ctx.createMediaElementSource(a);
        src.connect(ctx.destination);
        audioCtxRef.current = ctx;
      }
    } catch { /* Web Audio not available — plain <audio> playback still works. */ }

    const onTime = () => {
      if (a.duration) setProgress(a.currentTime / a.duration);
      setDuration(a.duration || 0);
    };
    // what happens at the end of a song is decided by the latest render (endRef below):
    // a listener set once here would only ever see the empty queue of the first render
    const onEnd = () => endRef.current && endRef.current();
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iOS/Safari keeps a newly-created AudioContext (or one backgrounded by a
  // screen lock/app switch) "suspended" until resume() is called synchronously
  // inside a real user-gesture event — an await/microtask in between makes
  // Safari ignore the call. Any tap/click/key anywhere in the app resumes it.
  useEffect(() => {
    const resume = () => {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    window.addEventListener("click", resume);
    window.addEventListener("touchend", resume);
    window.addEventListener("keydown", resume);
    return () => {
      window.removeEventListener("click", resume);
      window.removeEventListener("touchend", resume);
      window.removeEventListener("keydown", resume);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const current = queue[index] || null;
  discoveryRef.current = !!current?.discovery;
  // Object URL for the currently-loaded offline (blob) source, if any — kept
  // outside React state since it's an imperative resource, not render data.
  const objectUrlRef = useRef(null);
  // Which track id the <audio> element's `src` is currently resolved for, so
  // toggling play/pause on the SAME track never re-hits the cache/network —
  // only an actual track change pays for a fresh getPlayableUrl() resolution.
  const resolvedForRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    if (!current) {
      a.pause();
      setPlaying(false);
      if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
      resolvedForRef.current = null;
      return;
    }

    if (resolvedForRef.current === `${current.id}|${current.audio_url}`) {
      // Same track — src is already set, just apply the play/pause toggle.
      if (playing) a.play().catch(() => setPlaying(false));
      else a.pause();
      return;
    }

    // Track changed: resolve its source (offline cache if available while
    // disconnected, otherwise the live signed URL) before touching playback,
    // so we never call play() against a stale/empty src.
    let cancelled = false;
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    (async () => {
      const url = await getPlayableUrl(current);
      if (cancelled) return;
      resolvedForRef.current = `${current.id}|${current.audio_url}`;   // a new link (e.g. the discovery full listen) reloads the sound
      if (!url) {
        // Offline and this track was never cached while online — can't play.
        setPlaying(false);
        return;
      }
      if (url.startsWith("blob:")) objectUrlRef.current = url;
      a.src = url;
      if (playing) a.play().catch(() => setPlaying(false));
      else a.pause();
    })();

    return () => { cancelled = true; };
  }, [current, playing]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  // Enforce the preview duration for non-owned tracks. The audio element's
  // src is already the server-truncated preview clip — /api/tracks/{id}/
  // stream slices out exactly [preview_start, preview_start+preview_duration]
  // from the real file server-side (see backend's _truncate_audio_preview),
  // so the clip we receive here already starts at position 0 == the
  // artist's chosen preview_start. Seeking to preview_start again here
  // would skip into (or past) a clip that's only preview_duration seconds
  // long — the cutoff is simply the clip's own elapsed time now.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    const isLocked = !current.owned && !current.is_free && current.price > 0 && !current.discovery;
    if (!isLocked) return;
    const dur = current.preview_duration || 30;
    const onTime = () => {
      if (a.currentTime >= dur) {
        a.pause();
        setPlaying(false);
        try { window.dispatchEvent(new CustomEvent("metazik:preview-end", { detail: current })); } catch { /* dispatch is best-effort */ }
      }
    };
    a.addEventListener("timeupdate", onTime);
    return () => {
      a.removeEventListener("timeupdate", onTime);
    };
  }, [current]);

  const playTrack = useCallback((track, list) => {
    const newQueue = list && list.length ? list : [track];
    const i = newQueue.findIndex((t) => t.id === track.id);
    setQueue(newQueue);
    setIndex(i >= 0 ? i : 0);
    setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    setPlaying((p) => !p);
  }, []);

  // Hard pause used by the anti-piracy guard.
  const pause = useCallback(() => {
    setPlaying(false);
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch { /* ignore */ } }
  }, []);

  // Fully dismiss the mini/full player bar (not just pause it).
  const close = useCallback(() => {
    setPlaying(false);
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch { /* ignore */ } }
    setQueue([]);
    setIndex(0);
  }, []);

  const skipNext = useCallback(() => {
    setIndex((i) => (queue.length ? (i + 1) % queue.length : i));
    setPlaying(true);
  }, [queue.length]);

  const skipPrev = useCallback(() => {
    setIndex((i) => (queue.length ? (i - 1 + queue.length) % queue.length : i));
    setPlaying(true);
  }, [queue.length]);

  // End of what was playing: an extract (song not bought) stops and invites to buy; a song
  // goes on to the next one of the list, and the player stops after the last one.
  useEffect(() => {
    endRef.current = () => {
      if (current?.discovery) {
        // the free full listen is used up: the song goes back to its extract (Buy button)
        const back = { ...current, audio_url: current.preview_audio_url || current.audio_url, discovery: false };
        setQueue((q) => q.map((tr, i) => (i === index ? back : tr)));
        setPlaying(false);
        return;
      }
      if (current && !current.owned && !current.is_free && current.price > 0 && !current.discovery) {
        setPlaying(false);
        try { window.dispatchEvent(new CustomEvent("metazik:preview-end", { detail: current })); } catch { /* best-effort */ }
        return;
      }
      if (index < queue.length - 1) { setIndex(index + 1); setPlaying(true); } else setPlaying(false);
    };
  });

  const seek = useCallback((ratio) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    // free discovery listen: it plays through, no going back
    if (discoveryRef.current && a.duration * ratio < a.currentTime) return;
    a.currentTime = a.duration * ratio;
  }, []);

  // Lock-screen / notification controls on phones and media keys on computers, so the
  // music can be controlled while the person is in another app or tab.
  useEffect(() => {
    const ms = typeof navigator !== "undefined" ? navigator.mediaSession : null;
    if (!ms || !current) return;
    try {
      // eslint-disable-next-line no-undef
      ms.metadata = new MediaMetadata({
        title: current.title || "", artist: current.artist_name || "", album: "Metazik",
        artwork: current.cover ? [{ src: resolveImg(current.cover), sizes: "512x512" }] : [],
      });
      ms.setActionHandler("play", () => setPlaying(true));
      ms.setActionHandler("pause", () => setPlaying(false));
      ms.setActionHandler("nexttrack", queue.length > 1 ? skipNext : null);
      ms.setActionHandler("previoustrack", queue.length > 1 ? skipPrev : null);
    } catch { /* older browsers: plain playback still works */ }
  }, [current, queue.length, skipNext, skipPrev]);

  useEffect(() => {
    const ms = typeof navigator !== "undefined" ? navigator.mediaSession : null;
    if (ms) { try { ms.playbackState = playing ? "playing" : "paused"; } catch { /* ignore */ } }
  }, [playing]);

  // Inaudible ultrasonic watermark — active whenever playback is on and a track
  // is loaded. Encodes a per-user hash so leaked audio can be traced.
  useUltrasonicWatermark(audioRef, audioCtxRef, Boolean(current && playing));

  const value = useMemo(
    () => ({ current, queue, playing, progress, duration, volume, playTrack, togglePlay, pause, close, skipNext, skipPrev, seek, setVolume }),
    [current, queue, playing, progress, duration, volume, playTrack, togglePlay, pause, close, skipNext, skipPrev, seek]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
}
